import { gzipSync, gunzipSync, strFromU8, strToU8 } from 'fflate';

export type TraceStep = { id: string; title: string; status: 'running' | 'completed' | 'failed'; startedAt: string; finishedAt?: string; input?: unknown; output?: unknown; error?: string };
export type TraceActivity = {
  key: string;
  title: string;
  completed: number;
  total: number;
  unit: string;
  status: 'running' | 'completed';
  startedAt: string;
  updatedAt: string;
};
export type TraceProgress = { concurrency: number; activities: TraceActivity[]; finalized: boolean };
export type DebugTrace = { id: string; jobId?: number; title: string; userKey: string; startedAt: string; finishedAt?: string; status: 'running' | 'completed' | 'failed'; steps: TraceStep[]; progress?: TraceProgress };

const KEY = 'graphite.debug-traces.v2';
const LEGACY_KEY = 'graphite.debug-traces.v1';
const INTERRUPTED = 'Обработка была прервана перезагрузкой или закрытием приложения.';

function bytesToBase64(bytes: Uint8Array) {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  return btoa(binary);
}

function base64ToBytes(value: string) {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function decode(value: string | null): DebugTrace[] {
  if (!value) return [];
  const json = value.startsWith('gzip:') ? strFromU8(gunzipSync(base64ToBytes(value.slice(5)))) : value;
  return JSON.parse(json) as DebugTrace[];
}

export function listTraces(): DebugTrace[] {
  try { return decode(localStorage.getItem(KEY) || localStorage.getItem(LEGACY_KEY)); } catch { return []; }
}

function save(traces: DebugTrace[]) {
  const retained = traces.slice(0, 30);
  if (!retained.length) {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_KEY);
    window.dispatchEvent(new Event('graphite:traces'));
    return;
  }
  while (retained.length) {
    try {
      localStorage.setItem(KEY, `gzip:${bytesToBase64(gzipSync(strToU8(JSON.stringify(retained)), { level: 6 }))}`);
      localStorage.removeItem(LEGACY_KEY);
      break;
    } catch {
      retained.pop();
    }
  }
  if (!retained.length) localStorage.removeItem(KEY);
  window.dispatchEvent(new Event('graphite:traces'));
}

function wasInterrupted(trace: DebugTrace) {
  return trace.status === 'running' || trace.steps.some((step) => step.error === INTERRUPTED);
}

export function beginOrResumeTrace(jobId: number, title: string, userKey: string) {
  const traces = listTraces();
  let trace = traces.find((item) => item.jobId === jobId);
  if (!trace) {
    const legacy = traces.filter((item) => item.jobId === undefined && item.title === title && item.userKey === userKey && wasInterrupted(item));
    trace = legacy[0];
    if (trace) {
      trace.jobId = jobId;
      for (const duplicate of legacy.slice(1)) traces.splice(traces.indexOf(duplicate), 1);
    }
  }
  if (!trace) {
    trace = { id: crypto.randomUUID(), jobId, title, userKey, startedAt: new Date().toISOString(), status: 'running', steps: [], progress: { concurrency: 1, activities: [], finalized: false } };
    traces.unshift(trace);
  } else {
    trace.title = title; trace.userKey = userKey; trace.status = 'running'; delete trace.finishedAt;
    for (const step of trace.steps) if (step.error === INTERRUPTED) {
      step.status = 'running'; delete step.finishedAt; delete step.error;
    }
    const index = traces.indexOf(trace);
    if (index > 0) traces.unshift(...traces.splice(index, 1));
  }
  save(traces);
  return trace.id;
}

export function configureTraceProgress(traceId: string, concurrency: number) {
  const traces = listTraces(); const trace = traces.find((item) => item.id === traceId);
  if (!trace) return;
  trace.progress ||= { concurrency: 1, activities: [], finalized: false };
  trace.progress.concurrency = Math.max(1, Math.floor(concurrency));
  trace.progress.finalized = false;
  save(traces);
}

export function updateTraceActivity(traceId: string, key: string, title: string, completed: number, total: number, unit = 'операций') {
  const traces = listTraces(); const trace = traces.find((item) => item.id === traceId);
  if (!trace) return;
  trace.progress ||= { concurrency: 1, activities: [], finalized: false };
  const now = new Date().toISOString();
  let activity = trace.progress.activities.find((item) => item.key === key);
  const safeTotal = Math.max(0, Math.floor(total));
  const safeCompleted = Math.max(0, Math.min(safeTotal, Math.floor(completed)));
  if (!activity) {
    activity = { key, title, completed: safeCompleted, total: safeTotal, unit, status: safeTotal > 0 && safeCompleted >= safeTotal ? 'completed' : 'running', startedAt: now, updatedAt: now };
    trace.progress.activities.push(activity);
  } else {
    const restarting = activity.status === 'completed' && safeCompleted < safeTotal;
    activity.title = title; activity.completed = safeCompleted; activity.total = safeTotal; activity.unit = unit;
    activity.status = safeTotal > 0 && safeCompleted >= safeTotal ? 'completed' : 'running';
    activity.updatedAt = now;
    if (restarting || safeCompleted === 0) activity.startedAt = now;
  }
  save(traces);
}

export function finalizeTraceProgress(traceId: string) {
  const traces = listTraces(); const trace = traces.find((item) => item.id === traceId);
  if (!trace) return;
  trace.progress ||= { concurrency: 1, activities: [], finalized: true };
  trace.progress.finalized = true;
  save(traces);
}

export function traceProgressMetrics(trace: DebugTrace, now = Date.now()) {
  const activities = trace.progress?.activities || [];
  const completed = activities.reduce((sum, item) => sum + item.completed, 0);
  const total = activities.reduce((sum, item) => sum + item.total, 0);
  const active = activities.filter((item) => item.status === 'running' && item.total > item.completed);
  const rates = active.map((item) => item.completed / Math.max(1, (now - Date.parse(item.startedAt)) / 1_000)).filter((rate) => Number.isFinite(rate) && rate > 0);
  let etaSeconds: number | null = rates.length
    ? active.reduce((sum, item) => sum + item.total - item.completed, 0) / rates.reduce((sum, rate) => sum + rate, 0)
    : null;
  if (etaSeconds === null && total > completed) {
    const durations = trace.steps.filter((step) => step.status === 'completed' && step.finishedAt)
      .slice(-30).map((step) => Math.max(0, Date.parse(step.finishedAt!) - Date.parse(step.startedAt)) / 1_000).filter((value) => value > 0);
    if (durations.length) etaSeconds = (durations.reduce((sum, value) => sum + value, 0) / durations.length) * (total - completed) / Math.max(1, trace.progress?.concurrency || 1);
  }
  const rawPercent = total ? completed / total * 100 : 0;
  const percent = trace.status === 'completed' ? 100 : Math.min(99, rawPercent);
  return { completed, total, percent, etaSeconds, active };
}

export function beginStep(traceId: string, title: string, input?: unknown) {
  const traces = listTraces(); const trace = traces.find((item) => item.id === traceId);
  if (!trace) return crypto.randomUUID();
  const resumable = [...trace.steps].reverse().find((item) => item.title === title && item.status !== 'completed');
  if (resumable) {
    resumable.status = 'running'; resumable.startedAt = new Date().toISOString(); resumable.input = input;
    delete resumable.finishedAt; delete resumable.output; delete resumable.error;
    save(traces); return resumable.id;
  }
  const step = { id: crypto.randomUUID(), title, status: 'running' as const, startedAt: new Date().toISOString(), input };
  trace.steps.push(step); save(traces); return step.id;
}

export function finishStep(traceId: string, stepId: string, output?: unknown, error?: unknown) {
  const traces = listTraces(); const trace = traces.find((item) => item.id === traceId); const step = trace?.steps.find((item) => item.id === stepId);
  if (step) { step.finishedAt = new Date().toISOString(); step.status = error ? 'failed' : 'completed'; step.output = error ? undefined : output; step.error = error instanceof Error ? error.message : error ? String(error) : undefined; save(traces); }
}

export function finishTrace(traceId: string, error?: unknown) {
  const traces = listTraces(); const trace = traces.find((item) => item.id === traceId);
  if (trace) {
    trace.finishedAt = new Date().toISOString(); trace.status = error ? 'failed' : 'completed';
    if (trace.progress && !error) {
      trace.progress.finalized = true;
      for (const activity of trace.progress.activities) { activity.completed = activity.total; activity.status = 'completed'; activity.updatedAt = trace.finishedAt; }
    }
    save(traces);
  }
}

export function clearTraces() { save([]); }
